import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import CardScanButton, { cardFieldsToPrefill, isLowConfidence } from "../components/CardScan.jsx";
import { api } from "../lib/api.js";
import { Field, SelectWithOther, SaveButton, inputCls } from "../components/ui.jsx";

const SOURCES = ["Referral", "Website", "Bootcamp form", "LinkedIn", "Chamber of Commerce", "Event", "Business card"];

export default function NewContact() {
  const navigate = useNavigate();
  const { state } = useLocation(); // Home-screen card scan arrives as state.prefill
  const [f, setF] = useState({
    first_name: "", last_name: "", title: "", company: "", email: "", phone: "", address: "", website: "",
    type: "lead", source: "", notes: "",
    facebook_url: "", linkedin_url: "", twitter_url: "", instagram_url: "",
    ...(state?.prefill || {}),
  });
  const [scanned, setScanned] = useState(!!state?.prefill);
  const [lowConfidence, setLowConfidence] = useState(state?.lowConfidence || false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  function applyScan(fields) {
    const p = cardFieldsToPrefill(fields);
    // Anything the user already typed wins over the scan.
    setF((s) => ({ ...s, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, s[k] || v])) }));
    setScanned(true);
    setLowConfidence(isLowConfidence(fields));
  }

  async function submit(e) {
    e.preventDefault();
    if (!f.first_name.trim() && !f.last_name.trim()) return setError("First or last name is required.");
    setBusy(true);
    setError("");
    try {
      const res = await api("/api/crm/contacts", { method: "POST", body: f });
      navigate(`/crm/${res.id}`, { replace: true });
    } catch (err) {
      setError(err.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <Shell title="New contact" back>
      <div className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
        {!scanned && (
          <CardScanButton
            label="📇 Scan business card to auto-fill"
            className="w-full rounded-xl border-2 border-dashed border-navy-600/40 bg-navy-700/5 py-3 text-sm font-semibold text-navy-700 disabled:opacity-60"
            onExtracted={applyScan}
          />
        )}
        {scanned && (
          <p className={`rounded-xl px-4 py-2 text-sm ${lowConfidence ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>
            {lowConfidence ? "⚠️ Low-confidence read — double-check the fields." : "Card read ✓ — review and save."}
          </p>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input autoFocus={!scanned} value={f.first_name} onChange={set("first_name")} className={inputCls} />
            </Field>
            <Field label="Last name">
              <input value={f.last_name} onChange={set("last_name")} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Job title">
              <input value={f.title} onChange={set("title")} className={inputCls} placeholder="e.g. Ops Manager" />
            </Field>
            <Field label="Company">
              <input value={f.company} onChange={set("company")} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={f.type} onChange={set("type")} className={inputCls}>
                <option value="lead">Lead</option>
                <option value="client">Client</option>
                <option value="vendor">Vendor</option>
                <option value="contractor">Contractor</option>
                <option value="investor">Investor</option>
              </select>
            </Field>
            <Field label="Source">
              <SelectWithOther value={f.source} onChange={(v) => setF((s) => ({ ...s, source: v }))} options={SOURCES} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <input type="email" value={f.email} onChange={set("email")} className={inputCls} />
            </Field>
            <Field label="Phone">
              <input value={f.phone} onChange={set("phone")} className={inputCls} />
            </Field>
          </div>
          <Field label="Business address">
            <input value={f.address} onChange={set("address")} className={inputCls} placeholder="Street, city, state ZIP" />
          </Field>
          <Field label="Company website">
            <input value={f.website} onChange={set("website")} className={inputCls} placeholder="theirsite.com" inputMode="url" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Facebook">
              <input value={f.facebook_url} onChange={set("facebook_url")} className={inputCls} placeholder="URL or handle" inputMode="url" />
            </Field>
            <Field label="LinkedIn">
              <input value={f.linkedin_url} onChange={set("linkedin_url")} className={inputCls} placeholder="URL or handle" inputMode="url" />
            </Field>
            <Field label="X / Twitter">
              <input value={f.twitter_url} onChange={set("twitter_url")} className={inputCls} placeholder="URL or @handle" inputMode="url" />
            </Field>
            <Field label="Instagram">
              <input value={f.instagram_url} onChange={set("instagram_url")} className={inputCls} placeholder="URL or @handle" inputMode="url" />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <input value={f.notes} onChange={set("notes")} className={inputCls} placeholder="Website, where you met…" />
          </Field>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <SaveButton busy={busy}>Save contact</SaveButton>
        </form>
      </div>
    </Shell>
  );
}
